import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import { getMembershipLabel, getMembershipPriceByString, isUnderAge } from '../../utils/membership';
import { clubYear } from '@shared/membership';
import { familyPriceCents, FEES } from '@shared/fees';
import { householdFromMembers } from '@shared/familyLimits';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../ui/Toast';
import { logAuditEntry } from '../../utils/auditLog';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';
import { computeFailedPaymentEntityIds } from '../../utils/paymentFlags';
import { logDebug } from '../../utils/logger';
import { formatClubDate } from '../../utils/clubTime';
import FamilyRequestsTab from './familyRequests/FamilyRequestsTab';
import { countPendingFamilyChangeRequests } from './familyRequests/familyChangeRequestsClient';

// Use Cognito user pool auth for admin operations (updates/deletes require authentication)
const client = generateClient({ authMode: 'userPool' });

interface Member {
  id: string;
  memberNumber?: string;
  firstName: string;
  lastName: string;
  email: string;
  dateOfBirth: string;
  gender: string;
  phoneNumber: string;
  address: string;
  suburb?: string;
  state?: string;
  postCode?: string;
  shirtSize: string;
  membershipType: string;
  familyMembers?: string;
  isSponsor?: boolean;
  businessName?: string;
  sponsorTier?: string;
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianEmail?: string;
  guardianPhone?: string;
  status: 'PENDING' | 'VERIFIED' | 'REJECTED';
  paymentReceived: boolean;
  amountPaid?: number;
  paymentAdjustmentReason?: string;
  squarePaymentId?: string;
  createdAt: string;
  updatedAt: string;
}

const isSponsorType = (type: string) =>
  type === 'SPONSOR_GOLD' || type === 'SPONSOR_SILVER' || type === 'SPONSOR_BRONZE';

/**
 * Parses the pre-approval `familyMembers` JSON column, or [] on anything
 * unreadable. Hoisted above its first use (the "standard price" helpers
 * below) — the previous `parseFamilyMembers` further down the file is kept
 * for the family-roster display and does the same thing.
 */
const parseFamilyMembersJson = (fm: string | undefined): Array<{ dateOfBirth?: string }> => {
  if (!fm) return [];
  try {
    const parsed = JSON.parse(fm);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * The household price (D1/D2/D4) for a FAMILY application's roster, worked
 * out from the primary's DOB plus the stored `familyMembers` JSON, for the
 * club year the application was made in (its createdAt). Falls back to the
 * flat per-type price for everything else (sponsor/individual pricing is
 * unchanged, R2/out-of-scope).
 */
const expectedAmountForApplication = (app: Member): number => {
  if (app.membershipType !== 'FAMILY') return getMembershipPriceByString(app.membershipType);
  const year = clubYear(new Date(app.createdAt));
  const household = householdFromMembers(
    [
      { dateOfBirth: app.dateOfBirth, isPrimary: true },
      ...parseFamilyMembersJson(app.familyMembers),
    ],
    year
  );
  return familyPriceCents(household) / 100;
};

/**
 * The old flat family fee ($120, FEES.membership_fee_family), kept so
 * families who paid before this change aren't flagged "fee adjusted" just
 * because the standard price is now worked out per-household (#127 slice 2,
 * design.md §9.4). Not applicable to non-FAMILY types.
 */
const OLD_FLAT_FAMILY_PRICE = FEES.membership_fee_family / 100;

/**
 * True when `amountPaid` looks adjusted: it doesn't match the current
 * household price AND (for FAMILY) doesn't match the old flat $120 either.
 * Individuals and sponsors are unaffected — they only ever had one standard
 * price, so this reduces to the previous "amountPaid !== standardPrice"
 * check for them.
 */
const isAmountAdjusted = (app: Member, amountPaid: number): boolean => {
  const standard = expectedAmountForApplication(app);
  if (amountPaid === standard) return false;
  if (app.membershipType === 'FAMILY' && amountPaid === OLD_FLAT_FAMILY_PRICE) return false;
  return true;
};

interface PaymentRecordSummary {
  id: string;
  amountCents: number;
  currency: string;
  status: 'COMPLETED' | 'FAILED';
  entityId: string;
  timestamp: string;
}

const isPaymentRecordSummary = (record: unknown): record is PaymentRecordSummary => {
  if (!record || typeof record !== 'object') return false;
  const candidate = record as Partial<PaymentRecordSummary>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.entityId === 'string' &&
    typeof candidate.amountCents === 'number' &&
    Number.isFinite(candidate.amountCents) &&
    typeof candidate.currency === 'string' &&
    (candidate.status === 'COMPLETED' || candidate.status === 'FAILED') &&
    typeof candidate.timestamp === 'string' &&
    Number.isFinite(new Date(candidate.timestamp).getTime())
  );
};

const MemberApplications: React.FC = () => {
  const { user, userId } = useAuth();
  const [applications, setApplications] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<Member | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'PENDING' | 'VERIFIED' | 'REJECTED'>(
    'PENDING'
  );
  // Local, editable copy of the "Amount to Pay" field so admins can type freely
  // (including clearing it) without a DB write firing on every keystroke. It is
  // persisted once on blur. See persistAmount below.
  const [amountInput, setAmountInput] = useState<string>('');
  const [sendingPaymentLink, setSendingPaymentLink] = useState(false);
  const [failedPaymentIds, setFailedPaymentIds] = useState<Set<string>>(new Set());
  const [paymentRecords, setPaymentRecords] = useState<any[]>([]);
  // #127 slice 5: which top-level tab is shown — the existing applications
  // review (unchanged) or the new "Family requests" tab.
  const [activeTab, setActiveTab] = useState<'applications' | 'familyRequests'>('applications');
  const [pendingFamilyRequestCount, setPendingFamilyRequestCount] = useState(0);
  // #157 (S1): rows skipped by loadApplications because they came back null
  // with a GraphQL error (missing required field) — shown as an amber
  // notice rather than aborting the load.
  const [unreadableCount, setUnreadableCount] = useState(0);
  const toast = useToast();
  const showToast = (message: string, type: 'success' | 'error') => {
    if (type === 'error') toast.error(message);
  };

  useEffect(() => {
    loadApplications();
  }, [statusFilter]);

  // #127 slice 5: the "Family requests" tab badge count, loaded once on
  // mount independently of the applications list/filter above.
  useEffect(() => {
    countPendingFamilyChangeRequests()
      .then(setPendingFamilyRequestCount)
      .catch((err) => console.error('Error loading pending family request count:', err));
  }, []);

  // Re-seed the amount field whenever a different application is opened. Keyed on
  // id (not the whole object) so persisting an update does not clobber the field
  // while the admin is still interacting with the same application.
  useEffect(() => {
    if (selectedApplication) {
      const current =
        selectedApplication.amountPaid != null && selectedApplication.amountPaid !== 0
          ? selectedApplication.amountPaid
          : expectedAmountForApplication(selectedApplication);
      setAmountInput(String(current));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApplication?.id]);

  // Persist the typed amount once the field loses focus. Resets to the last
  // saved value on invalid/empty input, and skips the write (and audit log) when
  // the amount is unchanged.
  const persistAmount = async () => {
    if (!selectedApplication) return;
    const lastSaved =
      selectedApplication.amountPaid != null && selectedApplication.amountPaid !== 0
        ? selectedApplication.amountPaid
        : expectedAmountForApplication(selectedApplication);
    const newAmount = parseFloat(amountInput);
    if (isNaN(newAmount) || newAmount < 0) {
      setAmountInput(String(lastSaved));
      return;
    }
    if (newAmount === selectedApplication.amountPaid) return;
    try {
      await client.models.Member.update({
        id: selectedApplication.id,
        amountPaid: newAmount,
      });
      setSelectedApplication({
        ...selectedApplication,
        amountPaid: newAmount,
      });
      setAmountInput(String(newAmount));
      // Audit log
      try {
        await client.models.AuditLog.create({
          adminEmail: user?.email || '',
          adminSub: userId || '',
          actionType: 'FEE_ADJUSTED',
          targetEntityType: 'Member',
          targetEntityId: selectedApplication.id,
          description: `Fee adjusted to $${newAmount} for ${selectedApplication.firstName} ${selectedApplication.lastName} (${selectedApplication.email})`,
          metadata: JSON.stringify({ previousAmount: selectedApplication.amountPaid, newAmount }),
          timestamp: new Date().toISOString(),
        });
      } catch {
        /* non-fatal */
      }
    } catch (err) {
      showToast('Failed to update amount', 'error');
    }
  };

  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      const filter: any = statusFilter !== 'ALL' ? { status: { eq: statusFilter } } : {};
      // BUG FIXED 9 Aug 2026: this never followed nextToken, so a page-size
      // cutoff (Amplify Data's list() default, well under the real table
      // size once enough applications exist) could silently drop later
      // applications with no error shown — a real report: "5 payment
      // pending but applications only shows 2". Same class of bug already
      // found and fixed the previous day in MemberManagement.tsx; this file
      // was never touched then, so it kept the gap. Paginates fully now,
      // matching the PaymentRecord fetch immediately below (which was
      // already correct).
      //
      // #157 (S1): a row missing a required field comes back as null with
      // a GraphQL error rather than throwing. Keep paging, skip the null
      // rows (counted below for the amber notice), and never abort the
      // whole load on their account — one bad row used to make the entire
      // screen show "Failed to load applications".
      const allApplications: any[] = [];
      const allErrors: any[] = [];
      let unreadable = 0;
      let membersNextToken: string | null | undefined;
      do {
        const page: any = await client.models.Member.list({
          filter,
          nextToken: membersNextToken || undefined,
        });
        const items: any[] = page.data || [];
        const readable = items.filter((m) => m && typeof m === 'object');
        unreadable += items.length - readable.length;
        if (page.errors?.length) {
          allErrors.push(...page.errors);
          console.warn('MemberApplications: skipped unreadable application records', page.errors.map((e: any) => e?.path?.join('.')));
        }
        allApplications.push(...readable);
        membersNextToken = page.nextToken;
      } while (membersNextToken);

      setUnreadableCount(unreadable);
      const sorted = [...allApplications].sort(
        (a: any, b: any) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setApplications(sorted as Member[]);

      // Failed_Payment_Flag and detail history: paginate the global admin list
      // once per page (never once per member), so >500 records cannot silently
      // hide a later success/failure or truncate a member's history.
      try {
        const allPaymentRecords: any[] = [];
        let nextToken: string | null | undefined;
        do {
          const paymentResult = await client.models.PaymentRecord.list({ limit: 500, nextToken });
          allPaymentRecords.push(...(paymentResult.data || []).filter(isPaymentRecordSummary));
          nextToken = paymentResult.nextToken;
        } while (nextToken);
        setPaymentRecords(allPaymentRecords);
        setFailedPaymentIds(computeFailedPaymentEntityIds(allPaymentRecords));
      } catch (err) {
        console.error('Error loading payment records for Failed_Payment_Flag (non-fatal):', err);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (memberId: string) => {
    try {
      // Call Lambda function to generate and assign member number
      const generateResult = await client.mutations.generateMemberNumber({
        memberId,
      });

      if (generateResult.errors && generateResult.errors.length > 0) {
        showToast('Failed to generate member number: ' + generateResult.errors[0].message, 'error');
        return;
      }

      const response = parseLambdaResponse<{
        success?: boolean;
        memberNumber?: string;
        familyMemberNumbers?: Array<{ name: string; memberNumber: string }>;
        portalAccountMemberIds?: string[];
        error?: string;
      }>(generateResult?.data);
      if (!response || !response.success) {
        showToast(
          'Failed to generate member number: ' + (response?.error || 'Unknown error'),
          'error'
        );
        return;
      }

      const memberNumber = response.memberNumber;
      const familyMemberNumbers: { name: string; memberNumber: string }[] =
        response.familyMemberNumbers || [];
      const portalAccountMemberIds: string[] = response.portalAccountMemberIds || [];

      // Update member status to VERIFIED and activate, including renewal fields
      const now = new Date();
      const currentYear = clubYear(now);
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const expiryDate = `${currentYear}-12-31`;

      // Get the amount paid for the history entry
      const member = applications.find((m) => m.id === memberId);
      const amountPaid = member?.amountPaid || (member ? expectedAmountForApplication(member) : getMembershipPriceByString('ADULT'));

      const historyEntry = [
        {
          year: currentYear,
          amountPaid,
          renewalDate: today,
          type: 'NEW',
        },
      ];

      const result = await client.models.Member.update({
        id: memberId,
        status: 'VERIFIED',
        isActive: true,
        membershipYear: currentYear,
        membershipExpiryDate: expiryDate,
        renewalStatus: 'CURRENT',
        renewalDate: today,
        membershipHistory: JSON.stringify(historyEntry),
      });

      if (result.errors && result.errors.length > 0) {
        showToast('Failed to approve: ' + result.errors[0].message, 'error');
        return;
      }

      // Transition any family members staged at PENDING_PORTAL to VERIFIED,
      // which triggers member-email-trigger to create their Cognito accounts.
      for (const fmId of portalAccountMemberIds) {
        try {
          await client.models.Member.update({ id: fmId, status: 'VERIFIED' });
        } catch (err) {
          console.error(
            `Failed to transition family member ${fmId} to VERIFIED (continuing):`,
            err
          );
        }
      }

      // Build toast message based on membership type
      let toastMessage: string;
      if (member && member.membershipType === 'FAMILY' && familyMemberNumbers.length > 0) {
        const allNumbers = [
          `${memberNumber} (${member.firstName} ${member.lastName})`,
          ...familyMemberNumbers.map(
            (fm: { name: string; memberNumber: string }) => `${fm.memberNumber} (${fm.name})`
          ),
        ].join(', ');
        toastMessage = `Approved: ${allNumbers}`;
      } else {
        toastMessage = `Application approved successfully. Member number: ${memberNumber}.`;
      }

      // Email is now sent automatically via DynamoDB Stream trigger
      showToast(toastMessage, 'success');
      logAuditEntry(user?.email || '', userId || '', {
        actionType: 'MEMBER_APPROVED',
        targetEntityType: 'Member',
        targetEntityId: memberId,
        description: `Approved member application. Member number: ${memberNumber || 'pending'}`,
      });

      await loadApplications();
      setSelectedApplication(null);
    } catch (err) {
      console.error('Error approving application:', err);
      showToast('Failed to approve application', 'error');
    }
  };

  const handleReject = async (memberId: string) => {
    try {
      const result = await client.models.Member.update({
        id: memberId,
        status: 'REJECTED',
      });
      if (result.errors && result.errors.length > 0) {
        showToast('Failed to reject: ' + result.errors[0].message, 'error');
        return;
      }
      await loadApplications();
      setSelectedApplication(null);
      showToast('Application rejected', 'success');
      logAuditEntry(user?.email || '', userId || '', {
        actionType: 'MEMBER_REJECTED',
        targetEntityType: 'Member',
        targetEntityId: memberId,
        description: 'Rejected member application',
      });
    } catch (err) {
      showToast('Failed to reject application', 'error');
    }
  };

  const handleTogglePayment = async (member: Member) => {
    try {
      logDebug('MemberApplications', 'Attempting to update payment status for member:', member.id);
      logDebug('MemberApplications', 'Current paymentReceived:', member.paymentReceived);
      logDebug('MemberApplications', 'New paymentReceived:', !member.paymentReceived);

      const result = await client.models.Member.update({
        id: member.id,
        paymentReceived: !member.paymentReceived,
      });

      logDebug('MemberApplications', 'Update result:', result);

      if (result.errors && result.errors.length > 0) {
        console.error('GraphQL errors:', result.errors);
        showToast(`Failed to update payment status: ${result.errors[0].message}`, 'error');
        return;
      }
      await loadApplications();
      if (selectedApplication?.id === member.id) {
        setSelectedApplication({
          ...member,
          paymentReceived: !member.paymentReceived,
        });
      }
      showToast('Payment status updated successfully', 'success');

      // Audit log
      try {
        await client.models.AuditLog.create({
          adminEmail: user?.email || '',
          adminSub: userId || '',
          actionType: !member.paymentReceived ? 'PAYMENT_RECEIVED' : 'PAYMENT_REVERSED',
          targetEntityType: 'Member',
          targetEntityId: member.id,
          description: `Payment ${!member.paymentReceived ? 'marked as received' : 'reversed'} for ${member.firstName} ${member.lastName} (${member.email})${member.amountPaid ? ` — $${member.amountPaid}` : ''}${member.paymentAdjustmentReason ? ` (${member.paymentAdjustmentReason})` : ''}`,
          metadata: JSON.stringify({
            amountPaid: member.amountPaid,
            paymentAdjustmentReason: member.paymentAdjustmentReason,
            newStatus: !member.paymentReceived,
          }),
          timestamp: new Date().toISOString(),
        });
      } catch {
        /* non-fatal */
      }
    } catch (err) {
      console.error('Error updating payment status:', err);
      showToast(
        `Failed to update payment status: ${err instanceof Error ? err.message : 'Unknown error'}`,
        'error'
      );
    }
  };

  const handleSendPaymentLink = async (member: Member) => {
    setSendingPaymentLink(true);
    try {
      const result = await client.mutations.sendPaymentLink({ memberId: member.id });
      if (result.errors && result.errors.length > 0) {
        showToast(`Failed to send payment link: ${result.errors[0].message}`, 'error');
        return;
      }
      const response = parseLambdaResponse<{ success?: boolean; error?: string }>(result?.data);
      if (!response?.success) {
        showToast(response?.error || 'Failed to send payment link', 'error');
        return;
      }
      // toast.success is a silent no-op by design elsewhere in this file —
      // there's no other UI state change here to reflect the outcome, so a
      // visible confirmation is genuinely needed; a plain window.alert is
      // used instead of relying on the silent toast for this one action.
      window.alert(`Payment link sent to ${member.email}. It will expire in 48 hours.`);
    } catch (err) {
      console.error('Error sending payment link:', err);
      showToast('Failed to send payment link', 'error');
    } finally {
      setSendingPaymentLink(false);
    }
  };

  const filteredApplications = applications.filter(
    (app) =>
      // #157 (S1): a malformed row can still reach here with an optional
      // (or even normally required) field missing — null-safe so a
      // missing firstName/lastName/email filters that row out of a text
      // search instead of throwing and blanking the whole list.
      (app.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.email || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) =>
    formatClubDate(dateString, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      PENDING: 'bg-yellow-100 text-yellow-800',
      VERIFIED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const parseFamilyMembers = (fm: string | undefined) => {
    if (!fm) return [];
    try {
      return JSON.parse(fm);
    } catch {
      return [];
    }
  };

  return (
    <div className="space-y-6">
      {/* #127 slice 5: top-level tabs. "Applications" is the existing review
          flow (unchanged below); "Family requests" is the new tab. */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-6" role="tablist" aria-label="Member applications sections">
          <button
            role="tab"
            aria-selected={activeTab === 'applications'}
            onClick={() => setActiveTab('applications')}
            className={`${activeTab === 'applications' ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} min-h-[44px] whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm`}
          >
            Applications
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'familyRequests'}
            onClick={() => setActiveTab('familyRequests')}
            className={`${activeTab === 'familyRequests' ? 'border-fishing-blue text-fishing-blue' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} min-h-[44px] inline-flex items-center gap-2 whitespace-nowrap pb-3 px-1 border-b-2 font-medium text-sm`}
          >
            Family requests
            <span
              data-testid="family-requests-tab-badge"
              aria-label={`${pendingFamilyRequestCount} pending`}
              className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-fishing-blue text-white text-xs font-semibold"
            >
              {pendingFamilyRequestCount}
            </span>
          </button>
        </nav>
      </div>

      {activeTab === 'familyRequests' ? (
        <FamilyRequestsTab />
      ) : (
        <>
      {/* #157 (S1): malformed-row tolerance notice. */}
      {unreadableCount > 0 && (
        <div role="status" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          {unreadableCount === 1 ? '1 unreadable record' : `${unreadableCount} unreadable records`} were skipped.
        </div>
      )}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Member Applications</h2>
        <button
          onClick={loadApplications}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
          Refresh
        </button>
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name or email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status Filter</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
            >
              <option value="ALL">All Applications</option>
              <option value="PENDING">Pending</option>
              <option value="VERIFIED">Verified</option>
              <option value="REJECTED">Rejected</option>
            </select>
          </div>
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fishing-blue"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadApplications} className="text-fishing-blue hover:text-blue-700">
              Try Again
            </button>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
              />
            </svg>
            <p className="mt-2 text-sm text-gray-500">No applications found</p>
          </div>
        ) : (
          <>
            {/* Mobile card view */}
            <div className="block sm:hidden divide-y divide-gray-200">
              {filteredApplications.map((app) => (
                <div key={app.id} className="p-4 space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {app.firstName} {app.lastName}
                        {failedPaymentIds.has(app.id) && (
                          <span
                            className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                            title="Every payment attempt so far has failed"
                          >
                            Payment failed
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">{app.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(app.status)}`}
                      >
                        {app.status}
                      </span>
                      {app.paymentReceived && app.squarePaymentId && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Paid Online {app.amountPaid ? `$${app.amountPaid.toFixed(2)}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>{getMembershipLabel(app.membershipType)}</span>
                    <span>{formatDate(app.createdAt)}</span>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedApplication(app);
                    }}
                    className="w-full mt-1 px-3 py-2 text-sm font-medium text-fishing-blue bg-blue-50 rounded-md hover:bg-blue-100"
                  >
                    Review Application
                  </button>
                </div>
              ))}
            </div>
            {/* Desktop table view */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredApplications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {app.firstName} {app.lastName}
                        {failedPaymentIds.has(app.id) && (
                          <span
                            className="ml-2 px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800"
                            title="Every payment attempt so far has failed"
                          >
                            Payment failed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {app.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {getMembershipLabel(app.membershipType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {(() => {
                          const standardPrice = expectedAmountForApplication(app);
                          const displayAmount =
                            app.amountPaid != null && app.amountPaid !== 0
                              ? app.amountPaid
                              : standardPrice;
                          const isAdjusted =
                            app.amountPaid != null &&
                            app.amountPaid !== 0 &&
                            isAmountAdjusted(app, app.amountPaid);
                          return (
                            <span className={isAdjusted ? 'text-amber-600' : 'text-fishing-green'}>
                              ${displayAmount.toLocaleString()}
                              {isAdjusted && (
                                <span
                                  title={app.paymentAdjustmentReason || 'Adjusted'}
                                  className="ml-1 inline-flex items-center cursor-help"
                                >
                                  <svg
                                    className="h-4 w-4 text-amber-500"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                </span>
                              )}
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(app.status)}`}
                        >
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {app.paymentReceived ? (
                          app.squarePaymentId ? (
                            <span className="px-2 inline-flex text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              ✓ Square {app.amountPaid ? `$${app.amountPaid.toFixed(2)}` : ''}
                            </span>
                          ) : (
                            <span
                              className="px-2 inline-flex text-xs font-semibold rounded-full bg-amber-100 text-amber-800"
                              title="Marked as received manually by an admin — not verified by Square"
                            >
                              ✓ Manual
                            </span>
                          )
                        ) : (
                          <span className="px-2 inline-flex text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(app.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedApplication(app);
                          }}
                          className="text-fishing-blue hover:text-blue-700"
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Application Detail Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-end sm:items-center justify-center sm:p-4 z-50">
          <div className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-medium text-gray-900">Application Details</h3>
              <button
                onClick={() => setSelectedApplication(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Refund warning — shown for online-paid PENDING applications */}
              {selectedApplication.status === 'PENDING' &&
                selectedApplication.paymentReceived &&
                selectedApplication.squarePaymentId && (
                  <div className="bg-red-50 border border-red-300 rounded-lg p-4">
                    <p className="text-sm font-semibold text-red-800">
                      ⚠️ This member has paid their membership fee online ($
                      {selectedApplication.amountPaid?.toFixed(2)}). A refund must be processed via
                      the Square Dashboard before rejecting this application.
                    </p>
                    <p className="text-xs text-red-700 mt-1 font-mono">
                      Square ID: {selectedApplication.squarePaymentId}
                    </p>
                  </div>
                )}

              {/* Membership & Price */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-gray-600">Membership Type</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {getMembershipLabel(selectedApplication.membershipType)}
                    </p>
                  </div>
                  <div className="text-right">
                    {(() => {
                      const standardPrice = expectedAmountForApplication(selectedApplication);
                      const amountPaid =
                        selectedApplication.amountPaid != null &&
                        selectedApplication.amountPaid !== 0
                          ? selectedApplication.amountPaid
                          : standardPrice;
                      const isAdjusted = isAmountAdjusted(selectedApplication, amountPaid);
                      return isAdjusted ? (
                        <>
                          <p className="text-sm text-gray-600">
                            Standard:{' '}
                            <span className="line-through">${standardPrice.toLocaleString()}</span>
                          </p>
                          <p className="text-2xl font-bold text-amber-600">
                            ${amountPaid.toLocaleString()}
                          </p>
                          {selectedApplication.paymentAdjustmentReason && (
                            <p className="text-xs text-amber-600">
                              {selectedApplication.paymentAdjustmentReason}
                            </p>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-gray-600">Standard Price</p>
                          <p className="text-2xl font-bold text-fishing-green">
                            ${standardPrice.toLocaleString()}
                          </p>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Payment Adjustment */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Payment Details</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount to Pay
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        value={amountInput}
                        onChange={(e) => setAmountInput(e.target.value)}
                        onBlur={persistAmount}
                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adjustment Reason
                      </label>
                      <select
                        value={
                          selectedApplication.paymentAdjustmentReason?.startsWith('Other')
                            ? 'Other'
                            : selectedApplication.paymentAdjustmentReason || ''
                        }
                        onChange={async (e) => {
                          const val = e.target.value;
                          if (val === 'Other') {
                            setSelectedApplication({
                              ...selectedApplication,
                              paymentAdjustmentReason: 'Other',
                            });
                            try {
                              await client.models.Member.update({
                                id: selectedApplication.id,
                                paymentAdjustmentReason: 'Other',
                              });
                            } catch (err) {
                              showToast('Failed to update reason', 'error');
                            }
                            return;
                          }
                          try {
                            await client.models.Member.update({
                              id: selectedApplication.id,
                              paymentAdjustmentReason: val || null,
                            });
                            setSelectedApplication({
                              ...selectedApplication,
                              paymentAdjustmentReason: val,
                            });
                            showToast('Reason updated', 'success');
                          } catch (err) {
                            showToast('Failed to update reason', 'error');
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                      >
                        <option value="">No adjustment</option>
                        <option value="Committee member">Committee member</option>
                        <option value="Sponsor discount">Sponsor discount</option>
                        <option value="Financial hardship">Financial hardship</option>
                        <option value="Promotional offer">Promotional offer</option>
                        <option value="Volunteer discount">Volunteer discount</option>
                        <option value="Other">Other</option>
                      </select>
                      {selectedApplication.paymentAdjustmentReason?.startsWith('Other') && (
                        <input
                          type="text"
                          placeholder="Enter reason..."
                          defaultValue={
                            selectedApplication.paymentAdjustmentReason.startsWith('Other: ')
                              ? selectedApplication.paymentAdjustmentReason.slice(7)
                              : ''
                          }
                          onBlur={async (e) => {
                            const text = e.target.value.trim();
                            const reason = text ? `Other: ${text}` : 'Other';
                            try {
                              await client.models.Member.update({
                                id: selectedApplication.id,
                                paymentAdjustmentReason: reason,
                              });
                              setSelectedApplication({
                                ...selectedApplication,
                                paymentAdjustmentReason: reason,
                              });
                              showToast('Reason updated', 'success');
                            } catch (err) {
                              showToast('Failed to update reason', 'error');
                            }
                          }}
                          className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fishing-blue"
                        />
                      )}
                    </div>
                  </div>
                  {selectedApplication.paymentAdjustmentReason && (
                    <div className="text-sm text-gray-600 bg-yellow-50 border border-yellow-200 rounded p-2">
                      <strong>Reason:</strong> {selectedApplication.paymentAdjustmentReason}
                    </div>
                  )}
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Personal Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedApplication.memberNumber && (
                    <div className="col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <label className="text-sm font-medium text-gray-500">Member Number</label>
                      <p className="text-lg font-bold text-fishing-blue">
                        {selectedApplication.memberNumber}
                      </p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">First Name</label>
                    <p className="text-sm text-gray-900">{selectedApplication.firstName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Last Name</label>
                    <p className="text-sm text-gray-900">{selectedApplication.lastName}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-sm text-gray-900">{selectedApplication.email}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Phone</label>
                    <p className="text-sm text-gray-900">{selectedApplication.phoneNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                    <p className="text-sm text-gray-900">
                      {formatDate(selectedApplication.dateOfBirth)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Gender</label>
                    <p className="text-sm text-gray-900">{selectedApplication.gender}</p>
                  </div>
                  <div className="col-span-2">
                    <label className="text-sm font-medium text-gray-500">Address</label>
                    <p className="text-sm text-gray-900">
                      {selectedApplication.address}
                      {selectedApplication.suburb && (
                        <>
                          <br />
                          {selectedApplication.suburb}
                          {selectedApplication.state && `, ${selectedApplication.state}`}
                          {selectedApplication.postCode && ` ${selectedApplication.postCode}`}
                        </>
                      )}
                      {!selectedApplication.suburb &&
                        selectedApplication.postCode &&
                        ` ${selectedApplication.postCode}`}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Shirt Size</label>
                    <p className="text-sm text-gray-900">{selectedApplication.shirtSize}</p>
                  </div>
                </div>
              </div>

              {/* Guardian Details (Junior only) */}
              {selectedApplication.membershipType === 'JUNIOR' &&
                selectedApplication.guardianFirstName && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Guardian Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Guardian Name</label>
                        <p className="text-sm text-gray-900">
                          {selectedApplication.guardianFirstName}{' '}
                          {selectedApplication.guardianLastName}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Guardian Email</label>
                        <p className="text-sm text-gray-900">{selectedApplication.guardianEmail}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Guardian Phone</label>
                        <p className="text-sm text-gray-900">{selectedApplication.guardianPhone}</p>
                      </div>
                    </div>
                  </div>
                )}

              {/* Sponsor Details */}
              {isSponsorType(selectedApplication.membershipType) && (
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-3">Sponsor Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Business Name</label>
                      <p className="text-sm text-gray-900">
                        {selectedApplication.businessName || '—'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Sponsor Tier</label>
                      <p className="text-sm text-gray-900">
                        {selectedApplication.sponsorTier || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Family Members */}
              {(() => {
                const members = parseFamilyMembers(selectedApplication.familyMembers);
                return members.length > 0 ? (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">
                      Family Members ({members.length})
                    </h4>
                    <div className="space-y-3">
                      {members.map((member: any, index: number) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-sm font-medium text-gray-500">Name</label>
                              <p className="text-sm text-gray-900">
                                {member.firstName} {member.lastName}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">
                                Member Number
                              </label>
                              <p
                                className={`text-sm ${member.memberNumber ? 'font-bold text-fishing-blue' : 'text-gray-400 italic'}`}
                              >
                                {member.memberNumber || 'Pending'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">
                                Date of Birth
                              </label>
                              <p className="text-sm text-gray-900">
                                {formatDate(member.dateOfBirth)}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">Type</label>
                              <p className="text-sm text-gray-900">
                                {/* Derived from the date of birth, NOT from the
                                    `isAdult` flag. `isAdult` is set once by which
                                    button ("+ Add Adult" / "+ Add Child") was
                                    clicked during registration and is never
                                    re-derived from the DOB typed afterwards, so it
                                    could disagree with the DOB shown directly
                                    above — reported 15 Aug 2026 as adults showing
                                    as "Child". Uses isUnderAge, which applies the
                                    #127/D7 rule (16 or under on 1 January of the
                                    membership year via clubYear()) through
                                    @shared/membership, so this label, the
                                    competition divisions, and MemberDashboard all
                                    agree on one definition of junior. */}
                                {member.dateOfBirth
                                  ? isUnderAge(member.dateOfBirth, new Date())
                                    ? 'Junior'
                                    : 'Senior'
                                  : '—'}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-500">
                                Shirt Size
                              </label>
                              <p className="text-sm text-gray-900">{member.shirtSize}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Verification Status */}
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-3">Verification Status</h4>
                <div className="space-y-3">
                  {selectedApplication.paymentReceived && selectedApplication.squarePaymentId && (
                    <div className="bg-green-50 border border-green-300 rounded-lg p-3">
                      <p className="text-sm font-semibold text-green-800">
                        ✓ Payment verified by Square
                      </p>
                      <p className="text-xs text-green-700 mt-1 font-mono">
                        Square ID: {selectedApplication.squarePaymentId}
                      </p>
                    </div>
                  )}
                  {selectedApplication.paymentReceived && !selectedApplication.squarePaymentId && (
                    <div className="bg-amber-50 border border-amber-300 rounded-lg p-3">
                      <p className="text-sm font-semibold text-amber-800">
                        ⚠️ Marked as paid manually — not verified by Square.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Payment Received
                        {selectedApplication.squarePaymentId && (
                          <span className="text-xs text-gray-500 font-normal">
                            {' '}
                            (locked — verified by Square)
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-500">
                        Mark when payment of $
                        {(selectedApplication.amountPaid != null &&
                        selectedApplication.amountPaid !== 0
                          ? selectedApplication.amountPaid
                          : expectedAmountForApplication(selectedApplication)
                        ).toLocaleString()}{' '}
                        has been confirmed
                      </p>
                    </div>
                    <button
                      onClick={() => handleTogglePayment(selectedApplication)}
                      disabled={!!selectedApplication.squarePaymentId}
                      // This control had no accessible name at all — its only
                      // content is a decorative span, so screen readers
                      // announced an unlabelled button and assistive tech had
                      // no way to convey on/off state. Switch semantics fix
                      // that and, as a side effect, make it addressable in
                      // tests without brittle DOM traversal.
                      role="switch"
                      aria-checked={!!selectedApplication.paymentReceived}
                      aria-label="Mark payment received"
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                        selectedApplication.paymentReceived ? 'bg-fishing-green' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        aria-hidden="true"
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          selectedApplication.paymentReceived ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  {selectedApplication.status === 'PENDING' &&
                    !selectedApplication.paymentReceived && (
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Payment Recovery Link</p>
                          <p className="text-xs text-gray-500">
                            Email {selectedApplication.firstName} a link to pay online — useful if
                            their first payment attempt didn't go through. Expires in 48 hours.
                          </p>
                        </div>
                        <button
                          onClick={() => handleSendPaymentLink(selectedApplication)}
                          disabled={sendingPaymentLink}
                          className="px-3 py-2 text-sm font-medium text-white bg-fishing-blue rounded-md hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                        >
                          {sendingPaymentLink ? 'Sending…' : 'Send Payment Link'}
                        </button>
                      </div>
                    )}
                </div>
              </div>

              {/* Payment History — every PaymentRecord for this member,
                  including failed attempts, most recent first. */}
              {(() => {
                const history = paymentRecords
                  .filter((r: any) => r.entityId === selectedApplication.id)
                  .sort(
                    (a: any, b: any) =>
                      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
                  );
                if (history.length === 0) return null;
                return (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">
                      Payment History ({history.length})
                    </h4>
                    <div className="space-y-2">
                      {history.map((r: any) => (
                        <div
                          key={r.id}
                          className={`flex items-center justify-between p-3 rounded-lg border text-sm ${
                            r.status === 'COMPLETED'
                              ? 'bg-green-50 border-green-200'
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div>
                            <span
                              className={`font-semibold ${r.status === 'COMPLETED' ? 'text-green-800' : 'text-red-800'}`}
                            >
                              {r.status === 'COMPLETED' ? '✓ Completed' : '✗ Failed'}
                            </span>
                            <span className="text-gray-600 ml-2">
                              ${(r.amountCents / 100).toFixed(2)} {r.currency}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(r.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 sticky bottom-0 bg-white">
              <button
                onClick={() => setSelectedApplication(null)}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              {selectedApplication.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => handleReject(selectedApplication.id)}
                    className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => handleApprove(selectedApplication.id)}
                    disabled={!selectedApplication.paymentReceived}
                    title={
                      !selectedApplication.paymentReceived
                        ? 'Payment must be confirmed before approving'
                        : ''
                    }
                    className={`px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white ${
                      selectedApplication.paymentReceived
                        ? 'bg-fishing-green hover:bg-green-600'
                        : 'bg-gray-300 cursor-not-allowed'
                    }`}
                  >
                    Approve
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default MemberApplications;
